import React, { useContext, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/css';
import PropTypes from 'prop-types';
import { delay, get, invoke, toNumber } from 'lodash';
import { useApp } from '../../context/app-context';
import appLabels from '../../utils/appLabels';
import { OrderContext, OrderContextType } from '../Order/OrderTile';
import { ItemsContext, ItemsContextType } from './ItemsContext';
import { Spinner } from '../../common/Spinner/Spinner';
import { isMobileScreen } from '../../utils/utils';
import moment from 'moment';
import Modal from '../../common/Modal/Modal';
import formatString from '../../utils/formatString';

const QuantityContainer = styled.div`
  display: flex;
  padding: 0.5rem 0;
  box-sizing: border-box;
  border: 1px solid #7b1a27;
  border-radius: 100px;
  width: 8rem;
  align-items: stretch;
  justify-content: space-evenly;
  color: #7b1a27;
  user-select: none;

  & > button,
  & > .quantity {
    display: flex;
    align-items: center;
    height: 1.5rem;
  }

  & > button {
    cursor: pointer;
    border: 0;
    background: 0;
    color: #7b1a27;
  }

  & > button[disabled] svg {
    stroke: #f1f0ee;
  }

  & > .quantity p {
    margin: 0;
  }

  & > .quantity input {
    border: 0;
    max-width: 1.5rem;
    text-align: center;
    color: #7b1a27;
  }

  @media (max-width: 641px) {
    width: ${(props: { expanded: boolean }) => (props.expanded ? '8rem' : '40px')};
    height: ${(props: { expanded: boolean }) => (props.expanded ? 'initial' : '40px')};
    overflow: hidden;
    border: 1px solid #ddd9d5;
    z-index: ${(props: { expanded: boolean }) => (props.expanded ? '950' : 'initial')};
    position: ${(props: { expanded: boolean }) => (props.expanded ? 'absolute' : 'initial')};
    right: ${(props: { expanded: boolean }) => (props.expanded ? '1rem' : 'initial')};
    background-color: #ffffff;

    & > button {
      display: ${(props: { expanded: boolean; isOtaToSnsModal: boolean }) =>
        props.expanded || props.isOtaToSnsModal ? 'flex' : 'none'};
    }

    & > .quantity p {
      display: flex;
      flex-direction: column;
      align-items: center;
    }

    & > .quantity input {
      padding: 0;
      line-height: 12px;
      display: block;
    }

    & > .quantity .body-text {
      line-height: 12px;
    }
  }
`;

const expandableOverlay = css`
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  cursor: pointer;
  background-color: #f0eaeab5;
  z-index: 900;
`;

const mainContainerStyle = css`
  min-width: 40px;
`;

const quantityInputButtonStyle = css`
  background: 0;
  border: 0;
  padding: 0;
  max-width: 1.5rem;
  text-align: center;
  color: #7b1a27;
`;

const buttonContainer = css`
  display: flex;
  justify-content: center;
`;

const buttonsContainer = css`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const neverMindButtonStyle = css`
  color: #7b1a27;
  cursor: pointer;
  border: 0;
  background: 0;
  padding-top: 1rem;
  text-align: start;

  span {
    font-size: 16px;
    font-weight: 500;
  }
`;

const removeButtonStyle = css`
  background-color: #7b1a27;
  color: #fff;
  margin: 1.5rem 2rem 0;
  min-width: 18rem;
  font-size: 14px;
  line-height: 24px;
  border: 0;
  border-radius: 100px;
  padding: 0.5rem 1rem;
  cursor: pointer;
`;

const confirmButtonStyle = css`
  background-color: #7b1a27;
  color: #fff;
  border: 0;
  border-radius: 6.25rem;
  padding: 1rem 0.75rem;
  min-width: 14rem;
  cursor: pointer;
`;

const QuantityInput = (props) => {
  const { className, item } = props;
  const initialQuantity = get(props, 'quantity');
  const showDeleteIcon = get(props, 'showDeleteIcon', false);
  const showRemoveItemModal = get(props, 'showRemoveItemModal', false);
  const maxQuantity = get(props, 'maxQuantity', 20);

  const { showToast, setDisplaySlotChangesMessage } = useApp();
  const [inputQuantity, setInputQuantity] = useState(initialQuantity);
  const [quantity, setQuantity] = useState(initialQuantity);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(false);
  const [editableInput, setEditableInput] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const CurrentOrderContext = useContext(OrderContext) as OrderContextType;
  const currentItemsContext = useContext(ItemsContext) as ItemsContextType;
  const quantityResetOnError = get(props, 'quantityResetOnError', false);

  const snsNextOrderId = get(CurrentOrderContext, 'snsNextOrderId');
  const orderDate = get(CurrentOrderContext, 'orderDate');
  const nextOrderDate = moment(get(item, 'futureOrderDate'), 'YYYY-M-D');
  const skipItemdesc = formatString(appLabels.QuantitySelector.skipItemDescText, {
    itemDescription: get(props, 'item.itemDescription'),
    date: nextOrderDate.format('ddd MMM DD'),
  });
  const removeItemDesc = formatString(appLabels.QuantitySelector.removeItemDescText, {
    itemDescription: get(props, 'item.itemDescription'),
  });
  const isOtaToSnsModal = get(props, 'isOtaToSnsModal', false);

  const itemInfo = {
    nextOrderId: snsNextOrderId,
    nextOrderItemId: get(item, 'nextOrderItemId'),
    subscriptionId: get(item, 'subscriptionId'),
    orderDate: orderDate,
    isReplinishItem: get(item, 'isReplinishItem'),
    itemBpn: get(item, 'productBpn'),
  };

  const handleClickOut = (evt) => {
    if (!invoke(containerRef, 'current.contains', evt.target)) {
      setExpanded(false);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOut);

    return () => {
      document.removeEventListener('click', handleClickOut);
    };
  }, []);

  useEffect(() => {
    if (editableInput) {
      delay(() => invoke(inputRef, 'current.focus'), 0);
    }
  }, [editableInput]);

  useEffect(() => {
    setQuantity(initialQuantity);
    setInputQuantity(initialQuantity);
  }, [initialQuantity]);

  const updateQuantity = async (q: number | string) => {
    const newQuantity = [1, maxQuantity, +q].sort((a, b) => a - b)[1];
    if (!isOtaToSnsModal) {
      setLoading(true);
      // create payload for update
      const updateQuantityPayload = {
        subscriptionId: props.subscriptionId,
        quantity: newQuantity,
        nextOrderId: get(CurrentOrderContext, 'snsNextOrderId'), // Only used when inside an order context
        nextOrderItemId: get(props, 'item.nextOrderItemId'),
        isReplinishItem: get(props, 'item.isReplinishItem'),
        itemBpn: get(props, 'item.productBpn'),
        cartId: get(CurrentOrderContext, 'cartId'),
      };

      try {
        await invoke(currentItemsContext, 'updateQuantityFn', {
          ...props,
          ...updateQuantityPayload,
        });
        setQuantity(newQuantity);
        setInputQuantity(newQuantity);
        invoke(props, 'onChange', newQuantity);

        if (newQuantity > quantity) {
          analyticsEvents('quantity-increase');
        } else {
          analyticsEvents('quantity-decrease');
        }
      } catch (error) {
        quantityResetOnError && setInputQuantity(quantity);
        showToast('error', appLabels.QuantitySelector.updateFailedText);
      } finally {
        setLoading(false);
      }
    } else {
      setInputQuantity(newQuantity);
      setQuantity(newQuantity);
      invoke(props, 'onChange', newQuantity);
    }
  };

  const handleInput = async (evt) => {
    const newVal = get(evt, 'target.value', '').replace(/\D/g, '');
    setInputQuantity(+newVal);
  };

  const checkInputQuantity = async (inputQuantity) => {
    if (inputQuantity === 0 && showDeleteIcon) {
      setModal(true);
    } else {
      await updateQuantity(inputQuantity);
    }
  };

  const confirmInput = async (evt) => {
    if (evt.key === 'Enter') {
      await checkInputQuantity(inputQuantity);
      setEditableInput(false);
    }
  };

  const handleBlur = async () => {
    await checkInputQuantity(inputQuantity);
    setEditableInput(false);
  };

  const handleIncrement = async () => {
    if (quantity < maxQuantity) {
      const newQuantity = toNumber(quantity) + 1;
      await updateQuantity(newQuantity);
      setEditableInput(false);
    }
  };

  const handleDecrement = async () => {
    if (quantity > 1) {
      const newQuantity = toNumber(quantity) - 1;
      await updateQuantity(newQuantity);
      setEditableInput(false);
    }
  };

  const handleDelete = async () => {
    if (quantity <= 1) {
      setModal(true);
    }
  };

  const analyticsEvents = (linkName) => {
    try {
      if (window['AB']?.DATALAYER) {
        window['AB'].DATALAYER.scheduledAndSaveItemsLinkClick(linkName, props, orderDate);
      }
    } catch (error) {
      // Handle any errors that occur during execution
      console.error('@AnalyticsTeam error:', error);
    }
  };

  const handleContainerClick = (evt) => {
    if (isMobileScreen() && !expanded) {
      setExpanded(true);
    } else {
      evt.stopPropagation();
      evt.preventDefault();
      setEditableInput(true);
    }
  };

  const handleOverlayClick = () => {
    if (isMobileScreen()) {
      if (expanded) {
        setExpanded(false);
      }
    }
  };

  const attemptRemoveItem = async () => {
    setLoading(true);
    try {
      await invoke(currentItemsContext, 'skipItemOnce', itemInfo);

      setDisplaySlotChangesMessage(true);
      showToast('info', `Removed: ${get(item, 'itemDescription')}`);
      analyticsEvents('ota-remove-item|yes-remove-item');

      //refreshing the tab info only on success
      await invoke(currentItemsContext, 'fetchItemsInfo');
    } catch (err) {
      setInputQuantity(quantity);
      showToast('error', `Opps! We are unable to remove items right now. Please try again later.`);
      if (window['AB']?.DATALAYER) {
        window['AB'].DATALAYER.setError(
          'no-value',
          'item-summary|remove-item-error',
          'oops-we-are-unable-to-remove-items-right-now-please-try-again-later',
        );
      }
    } finally {
      setModal(false);
    }
    setLoading(false);
  };

  const attemptSkipItem = async () => {
    setLoading(true);
    try {
      await invoke(currentItemsContext, 'skipItemOnce', itemInfo);

      setDisplaySlotChangesMessage(true);
      showToast(
        'info',
        `${get(item, 'itemDescription')} skipped for ${moment(orderDate, 'YYYY-M-D').format('MMM D')} order.`,
      );

      //refreshing the tab info only on success
      await invoke(currentItemsContext, 'fetchItemsInfo');
    } catch (err) {
      setInputQuantity(quantity);
      showToast('error', `We couldn't complete your skip item request. Please try again later.`);
    } finally {
      setModal(false);
    }
    setLoading(false);
  };

  const DeleteIcon = ({ loading }) => {
    return (
      <button onClick={handleDelete} disabled={loading} aria-label={showRemoveItemModal ? 'Remove item' : 'skip once'}>
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M5.5 5.3C5.77614 5.3 6 5.52386 6 5.8V10.8C6 11.0762 5.77614 11.3 5.5 11.3C5.22386 11.3 5 11.0762 5 10.8V5.8C5 5.52386 5.22386 5.3 5.5 5.3Z"
            fill="#8C0E12"
          />
          <path
            d="M9.25 5.85C9.25 5.57386 9.02614 5.35 8.75 5.35C8.47386 5.35 8.25 5.57386 8.25 5.85V10.8C8.25 11.0762 8.47386 11.3 8.75 11.3C9.02614 11.3 9.25 11.0762 9.25 10.8V5.85Z"
            fill="#8C0E12"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M4 1.37879V2.77264H1.25C0.973858 2.77264 0.75 2.9965 0.75 3.27264C0.75 3.54879 0.973858 3.77264 1.25 3.77264H1.75V11.9726C1.75 13.0772 2.64543 13.9726 3.75 13.9726H10.25C11.3546 13.9726 12.25 13.0772 12.25 11.9726V3.77264H12.75C13.0261 3.77264 13.25 3.54879 13.25 3.27264C13.25 2.9965 13.0261 2.77264 12.75 2.77264H10V1.37879C10 0.893448 9.60655 0.5 9.12121 0.5H4.87879C4.39345 0.5 4 0.893448 4 1.37879ZM9 2.77264V1.5H5V2.77264H9ZM11.25 3.77264H2.75V11.9726C2.75 12.5249 3.19772 12.9726 3.75 12.9726H10.25C10.8023 12.9726 11.25 12.5249 11.25 11.9726V3.77264Z"
            fill="#8C0E12"
          />
        </svg>
      </button>
    );
  };
  const handleCancelSkipOrRemoveItem = () => {
    setInputQuantity(quantity);
    setModal(false);
  };

  return (
    <div className={mainContainerStyle} ref={containerRef} data-testid="quantity-input">
      {expanded ? (
        <div
          className={expandableOverlay}
          onClick={handleOverlayClick}
          role="presentation"
          aria-label={appLabels.QuantitySelector.quantityOverlayLabel}
          aria-description={appLabels.QuantitySelector.quantityOverlayLabel}
        />
      ) : null}

      <QuantityContainer className={className} expanded={expanded} isOtaToSnsModal={isOtaToSnsModal}>
        {showDeleteIcon && toNumber(quantity) <= 1 ? (
          <DeleteIcon loading={loading} />
        ) : (
          <button onClick={handleDecrement} disabled={quantity <= 1 || loading} aria-label="decrease quantity">
            <svg width="14" height="2" viewBox="0 0 14 2" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M0.5 1C0.5 1.27614 0.723858 1.5 1 1.5L13 1.5C13.2761 1.5 13.5 1.27614 13.5 1C13.5 0.723858 13.2761 0.5 13 0.5L1 0.5C0.723858 0.5 0.5 0.723858 0.5 1Z"
                fill="#7B1A27"
              />
            </svg>
          </button>
        )}
        <div className="quantity body-txt" aria-label="quantity" onClick={handleContainerClick}>
          {loading ? (
            <Spinner />
          ) : editableInput ? (
            <p>
              <input
                data-testid="quantity"
                type="text"
                ref={inputRef}
                onChange={handleInput}
                onKeyDown={confirmInput}
                onBlur={handleBlur}
                className="body-text"
                value={inputQuantity}
              />
              {quantity >= maxQuantity ? <span className="body-text">max</span> : null}
            </p>
          ) : (
            <p>
              <button
                aria-label={appLabels.QuantitySelector.editQuantityBtn}
                className={`body-text ${quantityInputButtonStyle}`}
                onClick={handleContainerClick}
              >
                {inputQuantity}
              </button>
              {quantity >= maxQuantity ? <span className="body-text">max</span> : null}
            </p>
          )}
        </div>
        <button onClick={handleIncrement} disabled={quantity >= maxQuantity || loading} aria-label="increase quantity">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M0.5 6.99989C0.5 7.27603 0.723858 7.49989 1 7.49989H13C13.2761 7.49989 13.5 7.27603 13.5 6.99989C13.5 6.72374 13.2761 6.49989 13 6.49989H1C0.723858 6.49989 0.5 6.72374 0.5 6.99989Z"
              fill="#7B1A27"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M7 13.5C7.27614 13.5 7.5 13.2761 7.5 13V1C7.5 0.723857 7.27614 0.5 7 0.5C6.72386 0.5 6.5 0.723857 6.5 1V13C6.5 13.2761 6.72386 13.5 7 13.5Z"
              fill="#7B1A27"
            />
          </svg>
        </button>
      </QuantityContainer>
      <Modal
        titleTextCenter={showRemoveItemModal}
        titleText={showRemoveItemModal ? 'Remove item' : 'Skip once'}
        description={showRemoveItemModal ? removeItemDesc : skipItemdesc}
        onClose={handleCancelSkipOrRemoveItem}
        show={modal}
        showRemoveItemModal={showRemoveItemModal}
        maxWidth={showRemoveItemModal ? 24 : 28}
      >
        <div className={showRemoveItemModal ? buttonsContainer : buttonContainer}>
          <button
            className={`${showRemoveItemModal ? removeButtonStyle : confirmButtonStyle} body-text`}
            onClick={showRemoveItemModal ? attemptRemoveItem : attemptSkipItem}
            disabled={loading}
            aria-label={showRemoveItemModal ? 'Confirm item Remove' : 'Confirm item skip'}
          >
            {loading ? '...' : showRemoveItemModal ? 'Yes, remove item' : 'Confirm'}
          </button>
          {showRemoveItemModal && (
            <button
              className={neverMindButtonStyle}
              data-testid="Never-mind"
              onClick={() => {
                handleCancelSkipOrRemoveItem();
                analyticsEvents('ota-remove-item|never-mind');
              }}
              aria-label="Close"
            >
              <span className="link-sm">Never mind</span>
            </button>
          )}
        </div>
      </Modal>
    </div>
  );
};

QuantityInput.propTypes = {
  quantity: PropTypes.number.isRequired,
  showDeleteIcon: PropTypes.bool,
  showRemoveItemModal: PropTypes.bool,
  onChange: PropTypes.func,
  className: PropTypes.string,
  maxQuantity: PropTypes.number,
  subscriptionId: PropTypes.string.isRequired,
  item: PropTypes.object.isRequired,
  quantityResetOnError: PropTypes.bool,
  isOtaToSnsModal: PropTypes.bool,
};

export default QuantityInput;
